// Device compass heading (degrees clockwise from north) for heading-up map
// orientation — the web counterpart of the app's useCompassHeading hook.
//
// iOS Safari requires a permission prompt that must run inside a user gesture
// (the orientation button tap), and reports webkitCompassHeading on plain
// deviceorientation events. Chrome/Android reports absolute alpha on
// deviceorientationabsolute. Desktops have no sensor: start() succeeds but the
// callback never fires, so callers should treat heading-up as best-effort.

export function compassSupported() {
  return typeof DeviceOrientationEvent !== "undefined" &&
    (navigator.maxTouchPoints > 0 || "ontouchstart" in window)
}

export function createCompassWatcher(onHeading) {
  let handler = null

  async function start() {
    if (handler) return true
    try {
      if (typeof DeviceOrientationEvent !== "undefined" &&
          typeof DeviceOrientationEvent.requestPermission === "function") {
        if (await DeviceOrientationEvent.requestPermission() !== "granted") return false
      }
    } catch {
      return false
    }
    handler = e => {
      const h = headingFrom(e)
      if (h != null) onHeading(h)
    }
    // Both listeners: iOS only fires deviceorientation (with webkitCompassHeading),
    // Chrome only yields an absolute alpha on deviceorientationabsolute. headingFrom
    // returns null for the non-authoritative event, so there's no double-feed.
    window.addEventListener("deviceorientationabsolute", handler, true)
    window.addEventListener("deviceorientation", handler, true)
    return true
  }

  function stop() {
    if (!handler) return
    window.removeEventListener("deviceorientationabsolute", handler, true)
    window.removeEventListener("deviceorientation", handler, true)
    handler = null
  }

  return { start, stop }
}

function headingFrom(e) {
  if (e.webkitCompassHeading != null && e.webkitCompassHeading >= 0) return e.webkitCompassHeading
  if (e.absolute && e.alpha != null) return (360 - e.alpha) % 360
  return null
}
